import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiRequest } from '../../api/client';

interface ListEntry {
  companyName: string;
  description: string;
  location: string;
  careerLink: string | null;
}

interface PostMetadata {
  matchedKeywords?: string[];
  jobLinks?: string[];
  hasJobLinks?: boolean;
  location?: string;
  companyName?: string;
  fromConfiguredSource?: boolean;
  isListPost?: boolean;
  listEntries?: ListEntry[];
  companyCount?: number;
}

interface Post {
  id: string;
  sourceType: string;
  sourceUrl: string;
  authorName: string | null;
  authorHandle: string | null;
  authorUrl: string | null;
  content: string;
  title: string | null;
  status: string;
  createdAt: string;
  postedAt: string | null;
  metadata: PostMetadata | null;
}

const AdminPosts: React.FC = () => {
  const { getToken } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [sourceFilter, setSourceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [convertingPostId, setConvertingPostId] = useState<string | null>(null);
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Clean up authorHandle by removing query parameters and "posts" noise
  const cleanAuthorHandle = (handle: string | null): string => {
    if (!handle) return '';
    // Remove query parameters
    const cleaned = handle.split('?')[0];
    // Skip if it's just "posts" or other generic terms
    if (cleaned === 'posts' || cleaned === 'feed') return '';
    return cleaned;
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(statusFilter && { status: statusFilter }),
        ...(sourceFilter && { sourceType: sourceFilter }),
      });

      const data = await apiRequest<{ posts: Post[]; pagination: { pages: number } }>(
        `/posts?${params}`,
        {},
        token
      );
      setPosts(data.posts);
      setTotalPages(data.pagination.pages);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [getToken, statusFilter, sourceFilter, page]);

  const updateStatus = async (postId: string, status: string) => {
    try {
      const token = await getToken();
      await apiRequest(`/posts/${postId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }, token);
      // Update local state instead of refetching
      setPosts(prev => prev.map(post =>
        post.id === postId ? { ...post, status } : post
      ));
    } catch (err: any) {
      alert(err.message);
    }
  };

  const convertToJob = async (postId: string) => {
    try {
      const token = await getToken();
      const result = await apiRequest<{ job: any; message: string }>(`/posts/${postId}/convert`, {
        method: 'POST',
      }, token);

      console.log(result.message, 'Job ID:', result.job.id);
      // Update local state instead of refetching
      setPosts(prev => prev.map(post =>
        post.id === postId ? { ...post, status: 'CONVERTED' } : post
      ));
    } catch (err: any) {
      console.error('Error converting post:', err.message);
    }
  };

  const convertListPost = async (postId: string) => {
    try {
      setConvertingPostId(postId);
      const token = await getToken();
      const result = await apiRequest<{ jobsCreated: number; errors?: string[] }>(`/posts/${postId}/convert-list`, {
        method: 'POST',
      }, token);

      console.log('Convert result:', result);
      if (result.errors && result.errors.length > 0) {
        console.error('Conversion errors:', result.errors);
      }

      // Update local state instead of refetching
      setPosts(prev => prev.map(post =>
        post.id === postId ? { ...post, status: 'CONVERTED' } : post
      ));
    } catch (err: any) {
      console.error('Convert list post error:', err);
    } finally {
      setConvertingPostId(null);
    }
  };

  const toggleSelectPost = (postId: string) => {
    setSelectedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPosts.size === posts.length) {
      setSelectedPosts(new Set());
    } else {
      setSelectedPosts(new Set(posts.map(p => p.id)));
    }
  };

  const deleteSelected = async () => {
    if (selectedPosts.size === 0) return;

    if (!window.confirm(`Delete ${selectedPosts.size} selected post(s)?`)) {
      return;
    }

    try {
      setIsDeleting(true);
      const token = await getToken();
      await apiRequest('/posts/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selectedPosts) }),
      }, token);

      // Update local state instead of refetching
      setPosts(prev => prev.filter(post => !selectedPosts.has(post.id)));
      setSelectedPosts(new Set());
    } catch (err: any) {
      alert(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const sourceTypes = ['TWITTER', 'LINKEDIN', 'REDDIT', 'HACKERNEWS', 'DISCORD', 'TELEGRAM', 'OTHER'];
  const statuses = ['PENDING', 'PROCESSING', 'CONVERTED', 'SKIPPED', 'DUPLICATE'];

  return (
    <div>
      <h1 className="text-2xl font-display font-bold text-amber-400 mb-6">Scraped Posts</h1>

      {/* Filters and Bulk Actions */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
          >
            <option value="">All Statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}
            className="bg-oak-700 text-cream-100 px-3 py-2 rounded-lg border border-oak-600"
          >
            <option value="">All Sources</option>
            {sourceTypes.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {!loading && posts.length > 0 && (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedPosts.size === posts.length && posts.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-oak-600 bg-oak-700 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-cream-200 text-sm">
                {selectedPosts.size === posts.length && posts.length > 0 ? 'Deselect All' : 'Select All'}
              </span>
            </label>

            {selectedPosts.size > 0 && (
              <div className="flex items-center gap-3">
                <span className="text-cream-200/70 text-sm">
                  {selectedPosts.size} selected
                </span>
                <button
                  onClick={deleteSelected}
                  disabled={isDeleting}
                  className="px-4 py-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-400 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-cream-200/50">Loading...</div>
      ) : posts.length === 0 ? (
        <div className="text-cream-200/50">No posts found</div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="card-wood rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={selectedPosts.has(post.id)}
                  onChange={() => toggleSelectPost(post.id)}
                  className="w-4 h-4 mt-1 rounded border-oak-600 bg-oak-700 text-amber-500 focus:ring-amber-500 cursor-pointer"
                />
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                <div>
                  <span className="text-xs bg-oak-600 text-cream-200 px-2 py-1 rounded mr-2">
                    {post.sourceType}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    post.status === 'PENDING' ? 'bg-amber-500/20 text-amber-400' :
                    post.status === 'CONVERTED' ? 'bg-green-500/20 text-green-400' :
                    post.status === 'SKIPPED' ? 'bg-gray-500/20 text-gray-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {post.status}
                  </span>
                </div>
                {(cleanAuthorHandle(post.authorHandle) || post.authorName) && post.authorUrl && (
                  <a
                    href={post.authorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-cream-200/70 hover:text-cream-100"
                  >
                    {post.authorName && <span>{post.authorName} </span>}
                    {cleanAuthorHandle(post.authorHandle) && (
                      <span className="text-amber-400">@{cleanAuthorHandle(post.authorHandle)}</span>
                    )}
                  </a>
                )}
              </div>

              {post.title && (
                <a
                  href={post.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-amber-400 hover:underline mb-2 block"
                >
                  {post.title}
                </a>
              )}

              <p className="text-cream-200/80 text-sm mb-3 whitespace-pre-wrap line-clamp-4">
                {post.content}
              </p>

              {/* Match reasons */}
              {post.metadata && (
                <div className="mb-3 p-2 bg-oak-700/50 rounded text-xs">
                  <div className="text-cream-200/50 mb-1">Match reasons:</div>
                  <div className="flex flex-wrap gap-1">
                    {post.metadata.fromConfiguredSource && (
                      <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                        Configured source
                      </span>
                    )}
                    {post.metadata.isListPost && (
                      <span className="bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded">
                        List post ({post.metadata.companyCount || post.metadata.listEntries?.length || 0} companies)
                      </span>
                    )}
                    {post.metadata.matchedKeywords && post.metadata.matchedKeywords.length > 0 && (
                      post.metadata.matchedKeywords.map((keyword, i) => (
                        <span key={i} className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                          {keyword}
                        </span>
                      ))
                    )}
                    {post.metadata.hasJobLinks && (
                      <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                        Has job links ({post.metadata.jobLinks?.length || 0})
                      </span>
                    )}
                    {post.metadata.location && (
                      <span className="bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                        {post.metadata.location}
                      </span>
                    )}
                    {!post.metadata.matchedKeywords?.length && !post.metadata.hasJobLinks && !post.metadata.fromConfiguredSource && (
                      <span className="text-cream-200/50">Direct job scrape</span>
                    )}
                  </div>
                  {post.metadata.jobLinks && post.metadata.jobLinks.length > 0 && (
                    <div className="mt-2">
                      <div className="text-cream-200/50 mb-1">Job links:</div>
                      {post.metadata.jobLinks.map((link, i) => (
                        <a
                          key={i}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-amber-400 hover:underline truncate"
                        >
                          {link}
                        </a>
                      ))}
                    </div>
                  )}
                  {/* List post entries */}
                  {post.metadata.isListPost && post.metadata.listEntries && post.metadata.listEntries.length > 0 && (
                    <div className="mt-3 border-t border-oak-600/50 pt-2">
                      <div className="text-cream-200/50 mb-2">Parsed companies:</div>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {post.metadata.listEntries.map((entry, i) => (
                          <div key={i} className="bg-oak-700/50 p-2 rounded">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <span className="font-medium text-cream-100">{entry.companyName}</span>
                                {entry.location && (
                                  <span className="text-cream-200/50 ml-2">({entry.location})</span>
                                )}
                              </div>
                              {entry.careerLink && (
                                <a
                                  href={entry.careerLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-amber-400 hover:underline shrink-0"
                                >
                                  Careers
                                </a>
                              )}
                            </div>
                            <div className="text-cream-200/70 mt-1">{entry.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  {post.postedAt && (
                    <span className="text-xs text-cream-200/50">
                      Posted {new Date(post.postedAt).toLocaleDateString()}
                    </span>
                  )}
                  <span className="text-xs text-cream-200/50">
                    Scraped {new Date(post.createdAt).toLocaleDateString()}
                  </span>
                </div>

                <div className="flex gap-2 items-center">
                  {post.status === 'PENDING' && (
                    <>
                      {post.metadata?.jobLinks && post.metadata.jobLinks.length > 0 ? (
                        <button
                          onClick={() => convertListPost(post.id)}
                          disabled={convertingPostId === post.id}
                          className="text-xs bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded hover:bg-cyan-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {convertingPostId === post.id ? 'Processing...' : `Convert ${post.metadata.jobLinks.length} Jobs`}
                        </button>
                      ) : (
                        <button
                          onClick={() => convertToJob(post.id)}
                          className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded hover:bg-green-500/30"
                        >
                          Convert to Job
                        </button>
                      )}
                    </>
                  )}
                  <select
                    value={post.status}
                    onChange={(e) => updateStatus(post.id, e.target.value)}
                    className="text-xs bg-oak-700 text-cream-200 px-2 py-1 rounded border border-oak-600"
                  >
                    {statuses.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-oak-700 text-cream-200 rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-3 py-1 text-cream-200/70">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 bg-oak-700 text-cream-200 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPosts;
